// ─── Garde calculée sur une PÉRIODE (hors affichage) ──────────────
// `useGardeAffichage` calcule la garde des jours VISIBLES : il est couplé à
// l'écran (cases du mois, semaine, jour sélectionné). L'export a besoin de la
// même chose sur une plage arbitraire, sans écran ni écoute permanente.
//
// La règle de priorité est la MÊME, et c'est le point important : surcharge
// stockée > répartition de vacances > rythme. La dupliquer ailleurs serait le
// meilleur moyen d'exporter une garde différente de celle affichée.
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@mop/core';
import { lireParents } from './garde';
import { lireRythmes, construireConfig } from './rythmesGarde';
import { gardePourDate } from './modelesGarde';
import { chargerVacancesConfig, gardeVacancesJour } from './vacances';

const pad = (n) => String(n).padStart(2, '0');
const _d = (dk) => { const [y, m, j] = String(dk).split('-').map(Number); return new Date(y, m - 1, j); };
const _dk = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Lecture PONCTUELLE des surcharges (l'export n'a pas à rester abonné).
async function lireSurcharges(calId) {
  const snap = await getDocs(collection(db, 'calendriers', calId, 'jours'));
  const m = {};
  snap.forEach(d => { const g = d.data().garde; if (g) m[d.id] = g; });
  return m;
}

// → { parJour: { dk: 'parent1'|'parent2' }, segments: [{ debut, fin, parent, nom }], parents }
// `segments` = suites de jours consécutifs chez le même parent : c'est cette
// forme que consomment l'ICS (un événement « journée » par segment) et le
// rapport, pas un jour isolé par ligne.
export async function calculerGardePeriode({ calId, debut, fin }) {
  const vide = { parJour: {}, segments: [], parents: null };
  if (!calId || !debut || !fin) return vide;

  const [surcharges, parents, rythmesCal, vacCfg] = await Promise.all([
    lireSurcharges(calId).catch(() => ({})),
    lireParents(calId).catch(() => null),
    lireRythmes(calId).catch(() => ({ rythmes: [], perso: [] })),
    chargerVacancesConfig(calId).catch(() => null),
  ]);

  const configs = [...(rythmesCal?.rythmes || [])]
    .filter(p => p.applique !== false)                     // toggle « Appliquer » par rythme
    .sort((a, b) => (a.debut < b.debut ? -1 : a.debut > b.debut ? 1 : 0))
    .map(p => ({ p, config: construireConfig(p.modeleId, p.debut, rythmesCal?.perso) }));

  const calculee = (dk) => {
    const gv = gardeVacancesJour(dk, vacCfg);
    if (gv) return gv;
    const item = configs.find(({ p }) => p.debut <= dk && (!p.fin || dk <= p.fin));
    return item?.config ? gardePourDate(item.config, dk) : null;
  };

  const parJour = {};
  for (let d = _d(debut); _dk(d) <= fin; d.setDate(d.getDate() + 1)) {
    const dk = _dk(d);
    const g = surcharges[dk] || calculee(dk);
    if (g) parJour[dk] = g;
  }

  const nomDe = (p) => (p === 'parent1' ? (parents?.parent1?.prenom || 'Parent 1')
                                        : (parents?.parent2?.prenom || 'Parent 2'));
  const segments = [];
  Object.keys(parJour).sort().forEach(dk => {
    const p = parJour[dk];
    const last = segments[segments.length - 1];
    const veille = _dk(new Date(_d(dk).getTime() - 86400000));
    // On ne prolonge que si le jour PRÉCÈDE immédiatement : sinon un trou dans
    // la garde (jour sans rythme) fusionnerait deux périodes distinctes.
    if (last && last.parent === p && last.fin === veille) last.fin = dk;
    else segments.push({ debut: dk, fin: dk, parent: p, nom: nomDe(p) });
  });

  return { parJour, segments, parents };
}

export default calculerGardePeriode;
